import {
  Grid,
  IconButton,
  Box,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
} from "@mui/material";

import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DeleteIcon from "@mui/icons-material/Delete";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import ActionHeaderCard from "../General/ActionHeaderCard";

import { useEffect, useState, useCallback } from "react";
import {
  getLegalFiles,
  deleteLegalFile,
  associateLawsuit,
  createLegalFileManual,
} from "../../services/LegalFileService";

import { getLawyers } from "../../services/LawyerService";
import DatagridCustom from "../General/DatagridCustom";
import { useAuth } from "../../context/AuthContext";
import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";
import OnlineQueryTooltip, {
  useOnlineQueryGate,
} from "../General/OnlineQueryGate";
import { useToast } from "../../context/ToastContext";

export default function ManagerLegalFile() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { online } = useOnlineQueryGate();

  const [legalFiles, setLegalFiles] = useState([]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  // Modal asociar demanda
  const [openModal, setOpenModal] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [lawsuitCode, setLawsuitCode] = useState("");
  const [associatingLawsuit, setAssociatingLawsuit] = useState(false);

  // Modal nuevo expediente
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [newLegalFileCode, setNewLegalFileCode] = useState("");
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [lawyers, setLawyers] = useState([]);
  const [loadingLawyers, setLoadingLawyers] = useState(false);
  const [savingLegalFile, setSavingLegalFile] = useState(false);
  const [sessionRequiredLawyer, setSessionRequiredLawyer] = useState(null);

  // Confirm delete
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const fetchLegalFiles = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const searchTerm = filterModel.quickFilterValues?.join(" ") || "";

      const res = await getLegalFiles({
        page: page + 1,
        perPage: pageSize,
        search: searchTerm,
      });

      setLegalFiles(res.data.data);
      setTotal(res.data.total);
    } catch (error) {
      console.error("[ManagerLegalFile] Error al cargar expedientes:", error);
      showToast(
        "No se pudieron cargar los expedientes. Intente de nuevo más tarde.",
      "error")
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  const fetchLawyers = useCallback(async () => {
    setLoadingLawyers(true);
    try {
      const res = await getLawyers({
        page: 1,
        perPage: 1000,
        search: "",
      });

      const lawyersData = res?.data?.data || res?.data || [];
      setLawyers(Array.isArray(lawyersData) ? lawyersData : []);
    } catch (error) {
      console.error("[ManagerLegalFile] Error al cargar abogados:", error);
      showToast("No se pudieron cargar los abogados.", "error")
    } finally {
      setLoadingLawyers(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchLegalFiles();
  }, [paginationModel, filterModel, fetchLegalFiles, showToast]);

  const handleView = (id) => {
    window.open(`/legal/file/${id}`, "_blank", "noopener,noreferrer");
  };

  const handleFilterModelChange = useCallback(
    (newFilterModel) => {
      const currentSearchTerm = filterModel.quickFilterValues?.join(" ") || "";
      const newSearchTerm = newFilterModel.quickFilterValues?.join(" ") || "";

      setFilterModel(newFilterModel);

      if (currentSearchTerm !== newSearchTerm) {
        setPaginationModel((prev) =>
          prev.page !== 0 ? { ...prev, page: 0 } : prev,
        );
      }
    },
    [filterModel],
  );

  const handleOpenDelete = (id) => {
    setSelectedDeleteId(id);
    setOpenDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDeleteId) return;

    try {
      const res = await deleteLegalFile(selectedDeleteId);
      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        setOpenDeleteConfirm(false);
        setSelectedDeleteId(null);
        await fetchLegalFiles();
      }
    } catch {
      showToast("Error al eliminar expediente.", "error")
    }
  };

  const handleOpenCreateModal = async () => {
    setOpenCreateModal(true);
    if (lawyers.length === 0) {
      await fetchLawyers();
    }
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    setNewLegalFileCode("");
    setSelectedLawyer(null);
  };

  const handleCreateLegalFile = async () => {
    if (!newLegalFileCode.trim()) {
      showToast("Debe ingresar el código del expediente.", "error")
      return;
    }

    if (!selectedLawyer?.id) {
      showToast("Debe seleccionar un abogado.", "error")
      return;
    }

    try {
      setSavingLegalFile(true);

      const res = await createLegalFileManual({
        code: newLegalFileCode.trim(),
        lawyer_id: selectedLawyer.id,
      });

      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        handleCloseCreateModal();
        await fetchLegalFiles();
      } else {
        showToast(res.data?.message || "No se pudo crear el expediente.", "error")
      }
    } catch (err) {
      if (err.response?.data?.code === "judicial_session_required") {
        setSessionRequiredLawyer(err.response.data.lawyer || selectedLawyer);
      } else {
        showToast(
          err.response?.data?.message || "Error al crear el expediente.",
          "error"
        );
      }
    } finally {
      setSavingLegalFile(false);
    }
  };

  const handleCloseAssociateModal = () => {
    setOpenModal(false);
    setSelectedFileId(null);
    setLawsuitCode("");
  };

  const handleAssociateLawsuit = async () => {
    if (!lawsuitCode.trim()) {
      showToast("Debe ingresar el código del título.", "error")
      return;
    }

    if (!selectedFileId) {
      showToast("No se encontró el expediente seleccionado.", "error")
      return;
    }

    try {
      setAssociatingLawsuit(true);

      const res = await associateLawsuit({
        code: lawsuitCode.trim(),
        legal_file_id: selectedFileId,
      });

      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        handleCloseAssociateModal();
        await fetchLegalFiles();
      } else {
        showToast(res.data?.message || "No se pudo asociar el título.", "error")
      }
    } catch (err) {
      showToast(
        err.response?.data?.message || "Error al asociar el título.",
      "error")
    } finally {
      setAssociatingLawsuit(false);
    }
  };

  const columns = [
    {
      field: "updated_at",
      headerName: "Fecha de Actualización",
      flex: 1,
      valueGetter: (params) => {
        const date = new Date(params);

        if (isNaN(date.getTime())) {
          return "Fecha inválida";
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${day}/${month}/${year} ${hours}:${minutes}`;
      },
    },
    { field: "code", headerName: "Código" },
    { field: "name", headerName: "Nombre", flex: 3 },
    { field: "status", headerName: "Estado", flex: 1 },
    { field: "location", headerName: "Ubicación", flex: 1 },
    {
      field: "lawyers",
      headerName: "Abogados",
      flex: 2,
      valueGetter: (params) => {
        const lawyers = params || [];
        return lawyers
          .map((lawyer) => `${lawyer.name} ${lawyer.lastname}`)
          .join(", ");
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      sortable: false,
      flex: 1.5,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handleView(params.row.id)}
            aria-label="ver"
            disabled={!hasPermission("ver detalle expediente")}
          >
            <VisibilityRoundedIcon />
          </IconButton>

          <IconButton
            color="error"
            onClick={() => handleOpenDelete(params.row.id)}
            aria-label="eliminar"
            disabled={!hasPermission("eliminar expediente")}
          >
            <DeleteIcon />
          </IconButton>

          <IconButton
            color="secondary"
            onClick={() => {
              setSelectedFileId(params.row.id);
              setOpenModal(true);
            }}
            aria-label="asociar título"
            disabled={
              !hasPermission("asociar demanda expediente") ||
              params.row.lawsuit_id !== null
            }
          >
            <AssignmentIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
        <Grid size={{ xs: 12 }}>

          <ActionHeaderCard
            icon={<AssignmentIcon />}
            title="Expedientes"
            description="Administrar expedientes registrados en el sistema."
            action={
              hasPermission("crear expediente") ? (
                <Button
                  onClick={handleOpenCreateModal}
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Nuevo expediente
                </Button>
              ) : null
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <DatagridCustom
              rows={legalFiles}
              columns={columns}
              title="Listado de Expedientes"
              description="Visualice los expedientes cargados en el sistema."
              icon={<FolderOpenRoundedIcon sx={{ fontSize: 32 }} />}
              paginationModel={paginationModel}
              onPaginationChange={setPaginationModel}
              total={total}
              loading={loading}
              filterModel={filterModel}
              onFilterChange={handleFilterModelChange}
              filterMode="server"
              isServerPagination={true}
            />
          </Box>
        </Grid>
      </Grid>

      <AppDialog
        open={openModal}
        onClose={handleCloseAssociateModal}
        maxWidth="sm"
        title="Asociar Título"
        description="Vincule un título electrónico al expediente seleccionado."
        icon={<GavelRoundedIcon />}
        actions={
          <>
            <Button
              onClick={handleCloseAssociateModal}
              sx={{
                borderRadius: 2,
                textTransform: "none",
              }}
              disabled={associatingLawsuit}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                fontWeight: 800,
                textTransform: "none",
                px: 3,
              }}
              onClick={handleAssociateLawsuit}
              disabled={associatingLawsuit}
            >
              {associatingLawsuit ? "Asociando..." : "Asociar"}
            </Button>
          </>
        }
      >
        <TextField
          label="Código de Título"
          placeholder="Ingrese el código exacto"
          fullWidth
          value={lawsuitCode}
          onChange={(e) => setLawsuitCode(e.target.value)}
          sx={{ mt: 2 }}
        />
      </AppDialog>

      <AppDialog
        open={openCreateModal}
        onClose={handleCloseCreateModal}
        maxWidth="sm"
        title="Nuevo Expediente"
        description="Agregue un expediente nuevo buscando la información desde la web judicial."
        icon={<AssignmentIcon />}
        actions={
          <>
            <Button
              onClick={handleCloseCreateModal}
              sx={{
                borderRadius: 2,
                textTransform: "none",
              }}
              disabled={savingLegalFile}
            >
              Cancelar
            </Button>

            <OnlineQueryTooltip show={!online}>
              <Button
                variant="contained"
                onClick={handleCreateLegalFile}
                disabled={savingLegalFile || !online}
                sx={{
                  borderRadius: 2,
                  fontWeight: 800,
                  textTransform: "none",
                  px: 3,
                }}
              >
                {savingLegalFile ? "Guardando..." : "Crear expediente"}
              </Button>
            </OnlineQueryTooltip>
          </>
        }
      >
        <TextField
          label="Código de Expediente"
          placeholder="Ingrese el código exacto del expediente"
          fullWidth
          value={newLegalFileCode}
          onChange={(e) => setNewLegalFileCode(e.target.value)}
          sx={{ mt: 2 }}
        />

        <Autocomplete
          options={lawyers}
          value={selectedLawyer}
          onChange={(_, value) => setSelectedLawyer(value)}
          loading={loadingLawyers}
          getOptionLabel={(option) =>
            option ? `${option.name} ${option.lastname}` : ""
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          sx={{ mt: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Abogado"
              placeholder="Seleccione un abogado"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingLawyers ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </AppDialog>

      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={() => {
          setOpenDeleteConfirm(false);
          setSelectedDeleteId(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Confirmar eliminación"
        description="¿Quieres eliminar este expediente? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="error"
      />

      <LawyerSessionRequiredDialog
        open={Boolean(sessionRequiredLawyer)}
        onClose={() => setSessionRequiredLawyer(null)}
        lawyer={sessionRequiredLawyer}
        onCaptured={() => {
          setSessionRequiredLawyer(null);
          handleCreateLegalFile();
        }}
      />
    </>
  );
}
